import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { isCycleNotFoundError, isCycleRequiredError } from "@/lib/cycleError";

/**
 * Centraliza o tratamento do 409 "cycle_required" e do 404 de ciclo inexistente
 * para os endpoints que dependem de cycleYear (profile/{code}, profile-details, product-pricing).
 */
export function useCycleRequiredHandler() {
  const { selectedCycleYear, setSelectedCycleYear } = useUser();
  const [availableCycles, setAvailableCycles] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCycleError = useCallback(
    (error: unknown): boolean => {
      const cycleRequired = isCycleRequiredError(error);
      if (cycleRequired) {
        setAvailableCycles(cycleRequired.availableCycles);
        setDialogOpen(true);
        return true;
      }

      if (isCycleNotFoundError(error) && selectedCycleYear) {
        if (String(error?.response?.data).includes('No profile details found')) {
          toast.warning(`Ciclo comercial ${selectedCycleYear}, sem produtos cadastrados.`);
        } else {
          setSelectedCycleYear(null);
          toast.warning("Ciclo comercial inexistente ou não publicado.");
        }
        return true;
      }

      return false;
    },
    [selectedCycleYear, setSelectedCycleYear],
  );

  const selectCycle = useCallback(
    (year: number) => {
      setSelectedCycleYear(year);
      setDialogOpen(false);
    },
    [setSelectedCycleYear],
  );

  return {
    selectedCycleYear,
    setSelectedCycleYear,
    availableCycles,
    dialogOpen,
    setDialogOpen,
    handleCycleError,
    selectCycle,
  };
}
